import { Col, Progress, Row, Spin, message } from "antd";
import queryString from "query-string";
import React from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import {
  APIError,
  Company,
  Country,
  Customer,
  Opportunity,
  OpportunityProspect,
  OpportunityProspectTypes,
  Product,
  SelectOptionsInterface,
  UpdatePaymentInformationFnProps,
} from "../helpers/shared-interfaces";

import * as Sentry from "@sentry/browser";
import { AxiosResponse } from "axios";
import {
  LOAD_OPPORTUNITY_LIGHT,
  STEPS,
  filterProductsByProspect,
  generateDisplayErrorMessage,
  mergeCompanyPaymentInformation,
  mergeCustomerPaymentInformation,
  request,
  responseHandler,
} from "../helpers/utilities";
import CompanyDetails from "./BusinessDevelopment/CompanyDetails";
import CompanyNameForm from "./BusinessDevelopment/CompanyNameForm";
import CompanyResults from "./BusinessDevelopment/CompanyResults";
import CustomerDetails from "./BusinessDevelopment/CustomerDetails";
import OpportunityPreview from "./BusinessDevelopment/OpportunityPreview";
import OpportunityProspectForm from "./BusinessDevelopment/OpportunityProspect";
import OpportunityReferralForm from "./BusinessDevelopment/OpportunityReferral";
import OpportunitySourceForm from "./BusinessDevelopment/OpportunitySource";
import OpportunitySummary from "./BusinessDevelopment/OpportunitySummary";
import PhoneNumberForm from "./BusinessDevelopment/PhoneNumberForm";
import ProductForm from "./BusinessDevelopment/ProductForm";

interface State {
  opportunityProspect: string;
  name: string;
  isLoading: boolean;
  opportunitySources: SelectOptionsInterface[];
  opportunityProspects: OpportunityProspect[];
  petraProducts: Product[];
  countries: Country[];
  opportunity: Opportunity | null;
  customer: Customer | null;
  lookupFoundCustomer: boolean;
  percentageComplete: number;
}

export enum Constants {
  referral = "Referral",
}

export enum PageNames {
  opportunityProspectOrReferrerPage = "Referrer Page / Opportunity Prospect Page",
  opportunityProspectPage = "Opportunity Prospect Page",
  referrerPagePrev = "Opportunity Source Page / Referrer Page",
  opportunitySourcePage = "Opportunity Source Page",
  identifierPage = "Identifier Page",
  customerDetailsOrProductPage = "Customer Details Page / Product Page",
  companyResultsPage = "Company Results Page",
  companyDetailsOrProductPage = "Company Details Page / Product Page",
  productPage = "Product Page",
  confirmationPage = "Confirmation Page",
  companyIdentifierPage = "Company Identifier Page",
}

const GET_OPPORTUNITY_DETAILS = `
  query GetOpportunityById($opportunityId: ID!) {
   opportunity(id: $opportunityId) {
    id
    state
    signedApplicationForm {
      id
      url
    }
    directDebitAuthorizationForm {
      id
      url
    }
    vestingRuleDocument {
      id
      url
    }
    npraForm {
      id
      url
    }
    employeeDatasheet {
      id
      url
    }
    companyQuery
    newEntity
    contributionType
    productPaymentType
    monthlyExpectedContribution
    lumpsumExpectedContribution
    signupReason
    sourceOfAwareness
    employerId
    referrer {
      id
      name
    }
    product {
      id
      name
      productTier {
        id
        name
      }
      productCategory {
        id
        name
      }
    }
    opportunitySource {
      id
      name
    }
    opportunityProspect {
      id
      name
    }
    company {
      id
      name
      petraId
      tinNumber
      ssnitNumber
      rocNumber
      companyPayoutAccounts {
        id
        name
        institutionName
        accountNumber
        accountType
        company {
          id
        }
        bankBranch {
          id
          name
          financialInstitution {
            id
            name
          }
        }
      }
      industrySector {
        id
        name
      }
      trustee {
        id
        name
      }
      tags {
        id
        name
      }
      companyAddresses{
        id
        isPrimary
        address {
          id
          streetName
          ghPost
          region
          poBox
          city
          country {
            id
            name
          }
        }
      }
      companyEmails{
        id
        isPrimary
        email {
          id
          address
        }
      }
      companyPhones {
        id
        isPrimary
        phone {
          id
          number
          type
        }
      }
      customers {
        id
        firstName
        lastName
        customerEmails {
          id
          isPrimary
          email {
            id
            address
          }
        }
        activeCustomerPhones {
          id
          isPrimary
          phone {
            id
            number
            type
          }
        }
      }
      companyContacts {
        id
        emailAddress
        mobileNumber
        contactName
        mobileNumber
        contactLevels {
          id
          name
        }
        customer {
          id
          petraId
          ssnitNumber
          dateOfBirth
          fullname
          maritalStatus {
            id
            name
          }
          middleName
          firstName
          gender
          lastName
          country {
            id
            name
          }
          customerEmails {
            id
            email {
              id
              address
            }
            isPrimary
          }
          activeCustomerPhones {
            id
            phone {
              id
              number
              type
            }
            isPrimary
          }
        }
    }
    }
    customer {
      id
      petraId
      ssnitNumber
      dateOfBirth
      firstName
      gender
      lastName
      middleName
      ghanaCardIdUrl
      ghanaCardIdBackUrl
      customerBackgroundInformation {
        fatherName
        fatherDeceased
        fatherPhoneNumber
        motherName
        motherDeceased
        motherPhoneNumber
        previousSsnitNumber
        hometown
        nextOfKinName
        nextOfKinPhoneNumber
        previousEmployer
      }
      ghanaCardNo
      idExpiryDate
      idIssueDate
      country {
        id
        name
      }
      signatureUrl
      photoIdUrl
      idType {
        id
        name
      }
      merged
      photoIdNumber
      maritalStatus {
        id
        name
      }
      tags {
        id
        name
      }
      customerDebitAccounts {
        id
        name
        isVerified
        institutionName
        bankAccountType
        accountNumber
        accountType
        amount
        expirationDate
        authorizationFormUrl
        bankBranch {
          id
          name
          financialInstitution {
            id
            name
          }
        }
      }
      customerPayoutAccounts {
        id
        name
        institutionName
        accountNumber
        accountType
        customer {
          id
        }
        bankBranch {
          id
          name
          financialInstitution {
            id
            name
          }
        }
      }
      customerAddresses {
        id
        address {
          id
          streetName
          ghPost
          region
          poBox
          city
          country {
            id
            name
          }
        }
        isPrimary
      }
      customerEmails {
        id
        email {
          id
          address
        }
        isPrimary
      }
      activeCustomerPhones {
        id
        phone {
          id
          number
          type
        }
        isPrimary
      }

    }
   }
  }
`;

const CONTINUE_OPPORTUNITY = `
  mutation ContinueOpportunity($opportunityId: ID!) {
    continueOpportunity(opportunityId: $opportunityId){
      opportunity {
        id
        state
        signedApplicationForm {
          id
          url
        }
        directDebitAuthorizationForm {
          id
          url
        }
        vestingRuleDocument {
          id
          url
        }
        npraForm {
          id
          url
        }
        employeeDatasheet {
          id
          url
        }
        companyQuery
        newEntity
        contributionType
        productPaymentType
        monthlyExpectedContribution
        lumpsumExpectedContribution
        signupReason
        sourceOfAwareness
        employerId
        referrer {
          id
          name
        }
        product {
          id
          name
          productTier {
            id
            name
          }
          productCategory {
            id
            name
          }
        }
        opportunitySource {
          id
          name
        }
        opportunityProspect {
          id
          name
        }
        company {
          id
          name
          petraId
          tinNumber
          companyPayoutAccounts {
            id
            name
            institutionName
            accountNumber
            accountType
            company {
              id
            }
            bankBranch {
              id
              name
              financialInstitution {
                id
                name
              }
            }
          }
          industrySector {
            id
            name
          }
          trustee {
            id
            name
          }
          tags {
            id
            name
          }
          companyAddresses{
            id
            isPrimary
            address {
              id
              streetName
              ghPost
              region
              poBox
              city
              country {
                id
                name
              }
            }
          }
          companyEmails{
            id
            isPrimary
            email {
              id
              address
            }
          }
          companyPhones {
            id
            isPrimary
            phone {
              id
              number
              type
            }
          }
          customers {
            id
            firstName
            lastName
            customerEmails {
              id
              isPrimary
              email {
                id
                address
              }
            }
            activeCustomerPhones {
              id
              isPrimary
              phone {
                id
                number
                type
              }
            }
          }
          companyContacts {
            id
            emailAddress
            mobileNumber
            contactName
            mobileNumber
            contactLevels {
              id
              name
            }
            customer {
              id
              petraId
              ssnitNumber
              dateOfBirth
              fullname
              maritalStatus {
                id
                name
              }
              middleName
              firstName
              gender
              lastName
              country {
                id
                name
              }
              customerEmails {
                id
                email {
                  id
                  address
                }
                isPrimary
              }
              activeCustomerPhones {
                id
                phone {
                  id
                  number
                  type
                }
                isPrimary
              }
            }
        }
        }
        customer {
          id
          petraId
          ssnitNumber
          dateOfBirth
          firstName
          gender
          lastName
          ghanaCardIdUrl
          ghanaCardIdBackUrl
          ghanaCardNo
          idExpiryDate
          idIssueDate
          middleName
          customerBackgroundInformation {
            fatherName
            fatherDeceased
            fatherPhoneNumber
            motherName
            motherDeceased
            motherPhoneNumber
            previousSsnitNumber
            hometown
            nextOfKinName
            nextOfKinPhoneNumber
            previousEmployer
          }
          signatureUrl
          photoIdUrl
          idType {
            id
            name
          }
          merged
          photoIdNumber
          tags {
            id
            name
          }
          customerDebitAccounts {
            id
            name
            isVerified
            institutionName
            bankAccountType
            accountNumber
            accountType
            amount
            expirationDate
            authorizationFormUrl
            bankBranch {
              id
              name
              financialInstitution {
                id
                name
              }
            }
          }
          customerPayoutAccounts {
            id
            name
            institutionName
            accountNumber
            accountType
            customer {
              id
            }
            bankBranch {
              id
              name
              financialInstitution {
                id
                name
              }
            }
          }
          country {
            id
            name
          }
          maritalStatus {
            id
            name
          }
          customerAddresses {
            id
            address {
              id
              streetName
              poBox
              ghPost
              region
              city
              country {
                id
                name
              }
            }
            isPrimary
          }
          customerEmails {
            id
            email {
              id
              address
            }
            isPrimary
          }
          activeCustomerPhones {
            id
            phone {
              id
              number
              type
            }
            isPrimary
          }

        }
      }
      errors {
        property
        description
      }
    }
  }
`;

const REVERT_OPPORTUNITY_MUTATION = `
  mutation RevertOpportunity($opportunityId: ID!) {
    revertOpportunity(opportunityId: $opportunityId){
      opportunity{
        id
        signedApplicationForm {
          id
          url
        }
        directDebitAuthorizationForm {
          id
          url
        }
        vestingRuleDocument {
          id
          url
        }
        npraForm {
          id
          url
        }
        employeeDatasheet {
          id
          url
        }
      state
      companyQuery
      newEntity
      contributionType
      productPaymentType
      monthlyExpectedContribution
      lumpsumExpectedContribution
      signupReason
      sourceOfAwareness
      employerId
      referrer {
        id
        name
      }
      product {
        id
        name
        productTier {
          id
          name
        }
        productCategory {
          id
          name
        }
      }
      opportunitySource {
        id
        name
      }
      opportunityProspect {
        id
        name
      }
      company {
        id
        name
        petraId
        tinNumber
        companyPayoutAccounts {
          id
          name
          institutionName
          accountNumber
          accountType
          company {
            id
          }
          bankBranch {
            id
            name
            financialInstitution {
              id
              name
            }
          }
        }
        industrySector {
          id
          name
        }
        trustee {
          id
          name
        }
        tags {
          id
          name
        }
        companyAddresses{
          id
          isPrimary
          address {
            id
            streetName
            ghPost
            region
            poBox
            city
            country {
              id
              name
            }
          }
        }
        companyEmails{
          id
          isPrimary
          email {
            id
            address
          }
        }
        companyPhones {
          id
          isPrimary
          phone {
            id
            number
            type
          }
        }
        customers {
          id
          firstName
          lastName
          customerEmails {
            id
            isPrimary
            email {
              id
              address
            }
          }
          activeCustomerPhones {
            id
            isPrimary
            phone {
              id
              number
              type
            }
          }
        }
      }
      customer {
        id
        petraId
        ssnitNumber
        dateOfBirth
        firstName
        gender
        lastName
        middleName
        signatureUrl
        photoIdUrl
        ghanaCardIdUrl
        ghanaCardIdBackUrl
        customerBackgroundInformation {
          fatherName
          fatherDeceased
          fatherPhoneNumber
          motherName
          motherDeceased
          motherPhoneNumber
          previousSsnitNumber
          hometown
          nextOfKinName
          nextOfKinPhoneNumber
          previousEmployer
        }
        ghanaCardNo
        idExpiryDate
        idIssueDate
        idType {
          id
          name
        }
        merged
        photoIdNumber
        country {
          id
          name
        }
        customerDebitAccounts {
          id
          name
          isVerified
          institutionName
          bankAccountType
          accountNumber
          accountType
          expirationDate
          authorizationFormUrl
          amount
          bankBranch {
            id
            name
            financialInstitution {
              id
              name
            }
          }
        }
        customerPayoutAccounts {
          id
          name
          institutionName
          accountNumber
          accountType
          customer {
            id
          }
          bankBranch {
            id
            name
            financialInstitution {
              id
              name
            }
          }
        }
        maritalStatus {
          id
          name
        }
        customerAddresses {
          id
          address {
            id
            streetName
            poBox
            ghPost
            region
            city
            country {
              id
              name
            }
          }
          isPrimary
        }
        customerEmails {
          id
          email {
            id
            address
          }
          isPrimary
        }
        activeCustomerPhones {
          id
          phone {
            id
            number
            type
          }
          isPrimary
        }
      }
      }
       errors {
        property
        description
      }
    }
  }
`;

const OPPORTUNITY_FORM_DATA = `
  query   {
    countries {
      id
      name
      isoCode
    }
    opportunitySources {
      id
      name
    }
    opportunityProspects {
        id
        name
      products {
        id
        name
        productTier {
          id
          name
        }
        productCategory {
          id
          name
        }
      }
    }
  }
`;

export default class NewOpportunity extends React.Component<
  RouteComponentProps,
  State
> {
  _isMounted = false;
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      percentageComplete: 0,
      customer: null,
      lookupFoundCustomer: false,
      opportunityProspects: [],
      opportunitySources: [],
      petraProducts: [],
      countries: [],
      opportunityProspect: "",
      name: "",
      isLoading: true,
      opportunity: null,
    };
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.finish = this.finish.bind(this);
    this.getView = this.getView.bind(this);

    this.handleOpportunityProspectChange =
      this.handleOpportunityProspectChange.bind(this);
    this.getOpportunityDetails = this.getOpportunityDetails.bind(this);
    this.continueOpportunity = this.continueOpportunity.bind(this);
    this.completeOpportunity = this.completeOpportunity.bind(this);
    this.setPercentage = this.setPercentage.bind(this);
    this.getNextViewName = this.getNextViewName.bind(this);
    this.getPreviousViewName = this.getPreviousViewName.bind(this);
  }

  handleOpportunityProspectChange(opportunityProspect: string): void {
    this.setState({ opportunityProspect });
  }

  async getOpportunityDetails(opportunityIdPassed?: string): Promise<void> {
    interface Response {
      data: {
        opportunity: Opportunity;
      };
    }
    const { opportunity } = this.state;
    this.setState({ isLoading: true });
    try {
      const { data }: AxiosResponse<Response> = await request({
        query: GET_OPPORTUNITY_DETAILS,
        variables: {
          opportunityId:
            opportunityIdPassed !== undefined
              ? opportunityIdPassed
              : opportunity && opportunity.id,
        },
      });
      if (data.data.opportunity && data.data.opportunity.id) {
        this.setState({ opportunity: data.data.opportunity });
      } else {
        message.error("Error occurred while loading opportunity");
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error(`An error occurred while trying to load opportunity - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async continueOpportunity(): Promise<void> {
    interface Response {
      opportunity: Opportunity;
      errors: APIError[];
    }

    const { opportunity } = this.state;
    this.setState({ isLoading: true });

    request({
      query: CONTINUE_OPPORTUNITY,
      variables: { opportunityId: opportunity && opportunity.id },
    })
      .then((response: AxiosResponse): void => {
        this.setState({ isLoading: false });
        const { mutation, err } = responseHandler<Response>(
          response,
          "continueOpportunity",
        );

        if (err) {
          message.error(generateDisplayErrorMessage(err));
          return;
        }

        if (mutation?.opportunity) {
          const opportunity = mutation.opportunity;
          this.setState({ opportunity });
        } else if (err) {
          Sentry.captureException(JSON.stringify(err, null, 4));
          message.error(generateDisplayErrorMessage(mutation?.errors));
        }
      })
      .catch((error) => {
        Sentry.captureException(JSON.stringify(error, null, 4));
        message.error("Failed To Continue.");
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  completeOpportunity(): void {
    interface Response {
      opportunity?: Opportunity;
      errors: APIError[];
    }

    const { opportunity } = this.state;
    this.setState({ isLoading: true });

    request({
      query: CONTINUE_OPPORTUNITY,
      variables: { opportunityId: opportunity && opportunity.id },
    })
      .then((response: AxiosResponse): void => {
        const { mutation, err } = responseHandler<Response>(
          response,
          "continueOpportunity",
        );

        if (mutation?.opportunity?.state === "completed") {
          this.setState({ opportunity: mutation.opportunity });
          this.props.history.push("/business-development");
        } else if (err) {
          message.error(generateDisplayErrorMessage(err));
          Sentry.captureException(JSON.stringify(err, null, 4));
          message.error(`Failed To Continue. - ${generateDisplayErrorMessage(err)}`);
        }
      })
      .catch((error) => {
        Sentry.captureException(JSON.stringify(error, null, 4));
        message.error("Failed To Complete.");
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  revertOpportunity(): void {
    interface Response {
      opportunity: Opportunity;
      errors: APIError[];
    }

    const { opportunity } = this.state;
    this.setState({ isLoading: true });
    request({
      query: REVERT_OPPORTUNITY_MUTATION,
      variables: { opportunityId: opportunity && opportunity.id },
    })
      .then((response: AxiosResponse): void => {
        const { mutation, err } = responseHandler<Response>(
          response,
          "revertOpportunity",
        );
        if (mutation?.opportunity) {
          const opportunity = mutation.opportunity;
          this.setState({ opportunity });
        } else if (err) {
          Sentry.captureException(JSON.stringify(err, null, 4));
          message.error(`Failed To Continue. - ${generateDisplayErrorMessage(err)}`);
        }
      })
      .catch((error) => {
        Sentry.captureException(JSON.stringify(error, null, 4));
        message.error("Failed To Revert.");
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  getOpportunityCreationData = () => {
    interface Response {
      data: {
        opportunitySources: [];
        opportunityProspects: [];
        countries: [];
      };
    }

    this.setState({ isLoading: true });

    request({
      query: OPPORTUNITY_FORM_DATA,
    })
      .then(({ data: { data } }: AxiosResponse<Response>): void => {
        this.setState({
          opportunitySources: data.opportunitySources,
          opportunityProspects: data.opportunityProspects,
          countries: data.countries,
        });
      })
      .catch((error) => {
        Sentry.captureException(error);
        message.error("Error fetching opportunity data");
      })
      .finally(() => {
        this.setState({
          isLoading: false,
        });
      });
  };

  setPercentage(percentage: number): void {
    this.setState({ percentageComplete: percentage });
  }

  next(): void {
    this.continueOpportunity();
  }

  prev(): void {
    this.revertOpportunity();
  }

  finish(): void {
    this.completeOpportunity();
  }

  loadOpportunity = async (): Promise<void> => {
    const { opportunity } = this.state;
    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<{
        data: {
          opportunity: Opportunity;
        };
      }> = await request({
        query: LOAD_OPPORTUNITY_LIGHT,
        variables: { opportunityId: opportunity?.id },
      });

      if (response.data.data.opportunity) {
        this.setState({
          opportunity: { ...opportunity, ...response.data.data.opportunity },
        });
      }
    } catch (e) {
      Sentry.captureException(e);
      message.error(`Failed to load opportunity - ${generateDisplayErrorMessage(e)}`);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getNextViewName(prospectType?: string): string | null {
    const opportunity = this.state.opportunity;
    if (opportunity) {
      if (opportunity.state === STEPS.started) {
        return PageNames.opportunityProspectOrReferrerPage;
      } else if (opportunity.state === STEPS.opportunitySourceProvided) {
        return PageNames.identifierPage;
      } else if (opportunity.state === STEPS.referrerProvided) {
        return PageNames.identifierPage;
      } else if (opportunity.state === STEPS.opportunityProspectProvided) {
        if (
          prospectType &&
          prospectType === OpportunityProspectTypes.institutionalClient
        ) {
          return PageNames.companyResultsPage;
        } else {
          return PageNames.customerDetailsOrProductPage;
        }
      } else if (opportunity.state === STEPS.customerIdentifierProvided) {
        return PageNames.productPage;
      } else if (opportunity.state === STEPS.customerInformationProvided) {
        return PageNames.confirmationPage;
      } else if (opportunity.state === STEPS.companyIdentifierProvided) {
        return PageNames.productPage;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  getPreviousViewName(): string | null {
    const opportunity = this.state.opportunity;
    if (opportunity) {
      if (opportunity.state === STEPS.started) {
        return null;
      } else if (opportunity.state === STEPS.opportunitySourceProvided) {
        return PageNames.opportunitySourcePage;
      } else if (opportunity.state === STEPS.referrerProvided) {
        return PageNames.referrerPagePrev;
      } else if (opportunity.state === STEPS.opportunityProspectProvided) {
        return PageNames.opportunityProspectPage;
      } else if (opportunity.state === STEPS.customerIdentifierProvided) {
        return PageNames.identifierPage;
      } else if (opportunity.state === STEPS.customerInformationProvided) {
        return PageNames.customerDetailsOrProductPage;
      } else if (opportunity.state === STEPS.productProvided) {
        return PageNames.productPage;
      } else if (opportunity.state === STEPS.companyIdentifierProvided) {
        return PageNames.companyIdentifierPage;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  extractQueryParamsFromUrl = (): { opportunityId: string; } => {
    const parsed = queryString.parse(window.location.search);
    return JSON.parse(JSON.stringify(parsed)) as { opportunityId: string; };
  };

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    this._isMounted &&
      (this.setState(this.props.location.state as State));
    const parsed = this.extractQueryParamsFromUrl();
    const opportunityIdPassed =
      this.state.opportunity?.id || parsed.opportunityId;
    if (opportunityIdPassed) {
      await this.getOpportunityDetails(opportunityIdPassed);
    }
    if (
      this.state.opportunitySources.length < 1 ||
      this.state.opportunityProspects.length < 1 ||
      this.state.countries.length < 1
    ) {
      this.getOpportunityCreationData();
    }
  }

  cancelEdit = () => {
    window.location.href = "/business-development/";
  };

  getView(): JSX.Element | null {
    const { opportunitySources, opportunityProspects, countries, opportunity } =
      this.state;

    if (opportunity) {
      const prospect: OpportunityProspect | null =
        opportunity.opportunityProspect;

      const products = filterProductsByProspect(
        opportunity,
        opportunityProspects,
      );

      switch (opportunity.state) {
        case STEPS.started:
          return (
            <OpportunitySourceForm
              opportunitySource={
                opportunity.opportunitySource
                  ? opportunity.opportunitySource.id
                  : undefined
              }
              getNextViewName={this.getNextViewName}
              getPreviousViewName={this.getPreviousViewName}
              setPercentage={this.setPercentage}
              opportunity={opportunity}
              opportunitySources={opportunitySources}
              next={this.next}
            />
          );
        case STEPS.opportunitySourceProvided:
          if (
            opportunity.opportunitySource &&
            opportunity.opportunitySource.name === Constants.referral
          ) {
            return (
              <OpportunityReferralForm
                opportunityReferrer={
                  opportunity.referrer ? opportunity.referrer.id : undefined
                }
                getNextViewName={this.getNextViewName}
                getPreviousViewName={this.getPreviousViewName}
                setPercentage={this.setPercentage}
                opportunity={opportunity}
                next={this.next}
                prev={this.prev}
              />
            );
          } else {
            return (
              <OpportunityProspectForm
                opportunityProspect={
                  opportunity.opportunityProspect
                    ? opportunity.opportunityProspect.id
                    : undefined
                }
                getNextViewName={this.getNextViewName}
                getPreviousViewName={this.getPreviousViewName}
                setPercentage={this.setPercentage}
                opportunity={opportunity}
                opportunityProspects={opportunityProspects}
                handleOpportunityProspectChange={
                  this.handleOpportunityProspectChange
                }
                next={this.next}
                prev={this.prev}
              />
            );
          }

        case STEPS.referrerProvided:
          return (
            <OpportunityProspectForm
              opportunityProspect={
                opportunity.opportunityProspect
                  ? opportunity.opportunityProspect.id
                  : undefined
              }
              getNextViewName={this.getNextViewName}
              getPreviousViewName={this.getPreviousViewName}
              setPercentage={this.setPercentage}
              opportunity={opportunity}
              opportunityProspects={opportunityProspects}
              handleOpportunityProspectChange={
                this.handleOpportunityProspectChange
              }
              next={this.next}
              prev={this.prev}
            />
          );

        case STEPS.opportunityProspectProvided:
          if (
            prospect &&
            prospect.name === OpportunityProspectTypes.retailClient
          ) {
            return (
              <PhoneNumberForm
                prev={this.prev}
                getNextViewName={this.getNextViewName}
                getPreviousViewName={this.getPreviousViewName}
                setPercentage={this.setPercentage}
                next={this.next}
                opportunity={opportunity}
              />
            );
          } else {
            return (
              <CompanyNameForm
                prev={this.prev}
                getNextViewName={this.getNextViewName}
                getPreviousViewName={this.getPreviousViewName}
                setPercentage={this.setPercentage}
                next={this.next}
                opportunity={opportunity}
                companyName={opportunity.companyQuery}
              />
            );
          }

        case STEPS.companyQueryProvided:
          return (
            <CompanyResults
              opportunity={opportunity}
              getNextViewName={this.getNextViewName}
              getPreviousViewName={this.getPreviousViewName}
              prev={this.prev}
              next={this.next}
              setPercentage={this.setPercentage}
            />
          );

        case STEPS.companyIdentifierProvided:
          if (!opportunity.newEntity) {
            return (
              <ProductForm
                opportunityPreview={
                  <OpportunityPreview opportunity={opportunity} />
                }
                getNextViewName={this.getNextViewName}
                getPreviousViewName={this.getPreviousViewName}
                setPercentage={this.setPercentage}
                petraProducts={products}
                opportunity={opportunity}
                next={this.next}
                prev={this.prev}
              />
            );
          }
          return (
            <CompanyDetails
              loadOpportunity={this.loadOpportunity}
              getPreviousViewName={this.getPreviousViewName}
              getNextViewName={this.getNextViewName}
              setPercentage={this.setPercentage}
              countries={countries}
              opportunity={opportunity}
              company={opportunity.company as Company}
              prev={this.prev}
              next={this.next}
              cancelEdit={this.cancelEdit}
              showOnboardingDetailsSection={true}
              updateCompanyPaymentInformation={
                this.updateCompanyPaymentInformation
              }
              companyContacts={opportunity.company?.companyContacts}
              isEditing={true}
            />
          );

        case STEPS.customerInformationProvided:
          return (
            <ProductForm
              opportunityPreview={
                <OpportunityPreview opportunity={opportunity} />
              }
              getNextViewName={this.getNextViewName}
              getPreviousViewName={this.getPreviousViewName}
              setPercentage={this.setPercentage}
              petraProducts={products}
              opportunity={opportunity}
              next={this.next}
              prev={this.prev}
              cancelEdit={this.cancelEdit}
            />
          );

        case STEPS.customerIdentifierProvided:
          return (
            <CustomerDetails
              loadOpportunity={this.loadOpportunity}
              updateCustomerPaymentInformation={
                this.updateCustomerPaymentInformation
              }
              opportunity={opportunity}
              customer={opportunity.customer as Customer}
              getNextViewName={this.getNextViewName}
              getPreviousViewName={this.getPreviousViewName}
              prev={this.prev}
              next={this.next}
              setPercentage={this.setPercentage}
              countries={countries}
              showBeneficiarySection={false}
              showOnboardingDetailsSection={true}
              cancelEdit={this.cancelEdit}
            />
          );

        case STEPS.companyInformationProvided:
          return (
            <ProductForm
              opportunityPreview={
                <OpportunityPreview opportunity={opportunity} />
              }
              getNextViewName={this.getNextViewName}
              getPreviousViewName={this.getPreviousViewName}
              setPercentage={this.setPercentage}
              petraProducts={products}
              opportunity={opportunity}
              next={this.next}
              prev={this.prev}
              cancelEdit={this.cancelEdit}
            />
          );

        case STEPS.productProvided:
          return (
            <OpportunitySummary
              getOpportunityDetails={this.getOpportunityDetails}
              getNextViewName={this.getNextViewName}
              getPreviousViewName={this.getPreviousViewName}
              opportunity={opportunity}
              prev={this.prev}
              finish={this.finish}
              setPercentage={this.setPercentage}
            />
          );
        default:
          return null;
      }
    } else {
      return null;
    }
  }

  updateCustomerPaymentInformation = (
    paymentInfo: UpdatePaymentInformationFnProps,
  ): void => {
    const { opportunity } = this.state;
    if (!opportunity?.customer) return;

    const updatedOpportunity: Opportunity = mergeCustomerPaymentInformation(
      opportunity,
      opportunity.customer,
      paymentInfo,
    );

    this.setState({ opportunity: updatedOpportunity });
  };

  updateCompanyPaymentInformation = (
    paymentInfo: UpdatePaymentInformationFnProps,
  ): void => {
    const { opportunity } = this.state;
    if (!opportunity?.company) return;

    const updatedOpportunity: Opportunity = mergeCompanyPaymentInformation(
      opportunity,
      opportunity.company,
      paymentInfo,
    );

    this.setState({ opportunity: updatedOpportunity });
  };

  render(): JSX.Element | null {
    const { isLoading, percentageComplete } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card header="NEW OPPORTUNITY">
          <Row>
            <Col span={24}>
              <div className="create-opportunity-container" style={{}}>
                <div className="progress-bar-container">
                  <Progress
                    strokeColor={
                      percentageComplete === 100
                        ? "#52c41a"
                        : "rgb(29, 55, 100)"
                    }
                    style={{ marginBottom: "10px" }}
                    percent={percentageComplete}
                  />
                </div>
                <div className="steps-content">{this.getView()}</div>
              </div>
            </Col>
          </Row>
        </Card>
      </Spin>
    );
  }
}
